import { all, call, put, takeLatest } from "redux-saga/effects";
import { AxiosCall, AxiosPost, AxiosPatch, AxiosDelete, AxiosFile } from "../../../utils/axios";
import * as actions from "./batchActions";

// Helper function to extract error message from Axios/DRF response
const extractErrorMessage = (error, defaultMessage = 'An error occurred') => {
    const data = error.response?.data;

    if (!data) {
        return error.message || defaultMessage;
    }

    // If data is a string, return it directly
    if (typeof data === 'string') {
        return data;
    }

    // Check common DRF error formats
    if (data.non_field_errors?.[0]) {
        return data.non_field_errors[0];
    }

    if (data.detail) {
        return data.detail;
    }

    if (data.error) {
        return data.error;
    }

    // Check for field-specific errors (e.g., { "batch_code": ["error message"] })
    const fieldErrors = Object.entries(data)
        .filter(([key, value]) => Array.isArray(value) && value.length > 0)
        .map(([key, value]) => `${key}: ${value[0]}`)
        .join('; ');

    if (fieldErrors) {
        return fieldErrors;
    }

    // Check for field-specific errors as strings (e.g., { "batch_code": "error message" })
    const stringFieldErrors = Object.entries(data)
        .filter(([key, value]) => typeof value === 'string')
        .map(([key, value]) => `${key}: ${value}`)
        .join('; ');

    if (stringFieldErrors) {
        return stringFieldErrors;
    }

    return error.message || defaultMessage;
};

// ==================== BATCHES ====================

function* fetchBatches(action) {
    try {
        const filters = action.payload || {};
        const queryParams = new URLSearchParams();

        Object.keys(filters).forEach(key => {
            const value = filters[key];
            if (value === '' || value === null || value === undefined) {
                return;
            }
            if (key === 'organization' && String(value).toLowerCase() === 'all') {
                return;
            }
            queryParams.append(key, value);
        });

        const queryString = queryParams.toString();
        const url = `/mycore/batch/list/${queryString ? `?${queryString}` : ''}`;

        const result = yield call(AxiosCall, url);
        yield put(actions.fetchBatchesSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchBatchesFailure(e.message));
    }
}

function* fetchBatchDetail(action) {
    try {
        const { batchId } = action.payload;
        const result = yield call(AxiosCall, `/mycore/${batchId}/batch/detail/`);
        yield put(actions.fetchBatchDetailSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchBatchDetailFailure(e.message));
    }
}

function* createBatch(action) {
    try {
        const result = yield call(AxiosPost, '/mycore/batch/add/', action.payload);
        yield put(actions.createBatchSuccess(result.data));
        // Refresh the list
        yield put(actions.fetchBatchesRequest());
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error creating batch');
        yield put(actions.createBatchFailure(errorMessage));
    }
}

function* updateBatch(action) {
    try {
        const { batchId, batchData } = action.payload;
        const result = yield call(AxiosPatch, `/mycore/${batchId}/batch/update/`, batchData);
        yield put(actions.updateBatchSuccess(result.data));
        // Refresh the list
        yield put(actions.fetchBatchesRequest());
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error updating batch');
        yield put(actions.updateBatchFailure(errorMessage));
    }
}

function* deleteBatch(action) {
    try {
        const { batchId } = action.payload;
        yield call(AxiosDelete, `/mycore/${batchId}/batch/delete/`);
        yield put(actions.deleteBatchSuccess(batchId));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error deleting batch');
        yield put(actions.deleteBatchFailure(errorMessage));
    }
}

function* updateBatchStatus(action) {
    try {
        const { batchId, status, ...extra } = action.payload;
        const result = yield call(AxiosPost, `/mycore/${batchId}/batch/status/`, { status, ...extra });
        yield put(actions.updateBatchStatusSuccess(result.data));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error updating batch status');
        yield put(actions.updateBatchStatusFailure(errorMessage));
    }
}

// ==================== NOTES ====================

function* fetchBatchNotes(action) {
    try {
        const { organizationId, batchId } = action.payload;
        let url = `/mycore/note/list/?organization_id=${organizationId}`;
        if (batchId) {
            url += `&batch_id=${batchId}`;
        }
        const result = yield call(AxiosCall, url);
        yield put(actions.fetchBatchNotesSuccess(result.data));
    } catch (e) {
        yield put(actions.fetchBatchNotesFailure(e.message));
    }
}

function* createBatchNote(action) {
    try {
        const result = yield call(AxiosPost, '/mycore/note/add/', action.payload);
        yield put(actions.createBatchNoteSuccess(result.data));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error creating note');
        yield put(actions.createBatchNoteFailure(errorMessage));
    }
}

function* updateBatchNote(action) {
    try {
        const { noteId, noteData } = action.payload;
        const result = yield call(AxiosPatch, `/mycore/${noteId}/note/update/`, noteData);
        yield put(actions.updateBatchNoteSuccess(result.data));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error updating note');
        yield put(actions.updateBatchNoteFailure(errorMessage));
    }
}

function* deleteBatchNote(action) {
    try {
        const { noteId } = action.payload;
        yield call(AxiosDelete, `/mycore/${noteId}/note/delete/`);
        yield put(actions.deleteBatchNoteSuccess(noteId));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error deleting note');
        yield put(actions.deleteBatchNoteFailure(errorMessage));
    }
}

// ==================== IMAGES ====================

function* fetchBatchImages(action) {
    try {
        const { batchId } = action.payload;
        const result = yield call(AxiosCall, `/mycore/image/list/?batch_id=${batchId}`);
        yield put(actions.fetchBatchImagesSuccess(result.data));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error fetching images');
        yield put(actions.fetchBatchImagesFailure(errorMessage));
    }
}

function* uploadBatchImage(action) {
    try {
        const formData = new FormData();
        Object.keys(action.payload).forEach(key => {
            formData.append(key, action.payload[key]);
        });

        const result = yield call(AxiosFile, '/mycore/image/add/', formData);
        yield put(actions.uploadBatchImageSuccess(result.data));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error uploading image');
        yield put(actions.uploadBatchImageFailure(errorMessage));
    }
}

function* deleteBatchImage(action) {
    try {
        const { imageId } = action.payload;
        yield call(AxiosDelete, `/mycore/${imageId}/image/delete/`);
        yield put(actions.deleteBatchImageSuccess(imageId));
    } catch (e) {
        const errorMessage = extractErrorMessage(e, 'Error deleting image');
        yield put(actions.deleteBatchImageFailure(errorMessage));
    }
}

// ==================== ROOT SAGA ====================

export default function* batchSaga() {
    yield all([
        // Batches
        takeLatest(actions.FETCH_BATCHES.REQUEST, fetchBatches),
        takeLatest(actions.FETCH_BATCH_DETAIL.REQUEST, fetchBatchDetail),
        takeLatest(actions.CREATE_BATCH.REQUEST, createBatch),
        takeLatest(actions.UPDATE_BATCH.REQUEST, updateBatch),
        takeLatest(actions.DELETE_BATCH.REQUEST, deleteBatch),
        takeLatest(actions.UPDATE_BATCH_STATUS.REQUEST, updateBatchStatus),

        // Notes
        takeLatest(actions.FETCH_BATCH_NOTES.REQUEST, fetchBatchNotes),
        takeLatest(actions.CREATE_BATCH_NOTE.REQUEST, createBatchNote),
        takeLatest(actions.UPDATE_BATCH_NOTE.REQUEST, updateBatchNote),
        takeLatest(actions.DELETE_BATCH_NOTE.REQUEST, deleteBatchNote),

        // Images
        takeLatest(actions.FETCH_BATCH_IMAGES.REQUEST, fetchBatchImages),
        takeLatest(actions.UPLOAD_BATCH_IMAGE.REQUEST, uploadBatchImage),
        takeLatest(actions.DELETE_BATCH_IMAGE.REQUEST, deleteBatchImage),
    ])
}
